<template>
  <div class="c-grid">
    <div class="c-grid__items">
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
        <CompaniesSurveyTemplateFormDisabledQuestion v-if="!meta.questionEditable" />
        <template v-else>
          <Info>{{ $t('components.CompaniesSurveyTemplateForm.question.item.info') }}</Info>
          <div v-for="(question, index) in questions" :key="index" :class="{ 'question': index !== 0 }">
            <KkBoxWithMenu
              :menu-list="menuList"
              @on-click-edit-menu="e => handleClickMenu({ type: e, index: index })"
            >
              <template #title>
                {{ $t('components.CompaniesSurveyTemplateForm.question.item.title') }}
              </template>
              <template #default>
                <div class="formParts">
                  <ValidationProvider v-slot="{ errors }"
                                      v-model="question.name"
                                      :vid="'questionName' + index"
                                      :name="$t('components.CompaniesSurveyTemplateForm.question.formLabels.question.name')"
                                      :rules="'required|max:' + maxLength['name']"
                  >
                    <label class="c-formLabel">
                      {{ labelTextFormat({ label: 'question.name', max: maxLength['name'], required: true, current: question.name }) }}
                    </label>
                    <TextField v-model="question.name" class="c-textField" :placeholder="$t('messages.noSettings')" />
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </div>
                <div class="formParts">
                  <ValidationProvider v-slot="{ errors }"
                                      v-model="question.description"
                                      :vid="'questionDescription' + index"
                                      :name="$t('components.CompaniesSurveyTemplateForm.question.formLabels.question.description')"
                                      :rules="'max:' + maxLength['description']"
                  >
                    <label class="c-formLabel">
                      {{ labelTextFormat({ label: 'question.description', max: maxLength['description'], required: false, current: question.description }) }}
                    </label>
                    <TextField v-model="question.description" class="c-textField" :placeholder="$t('messages.noSettings')" />
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </div>
                <div class="formParts">
                  <input
                    :id="'checkbox__questionRequired__' + index"
                    v-model="question.required"
                    class="c-checkbox__input"
                    type="checkbox"
                  >
                  <label class="c-checkbox__label" :for="'checkbox__questionRequired__' + index">
                    {{ $t('components.CompaniesSurveyTemplateForm.required') }}
                  </label>
                </div>
                <div class="formParts">
                  <label class="c-formLabel">
                    {{ $t('components.CompaniesSurveyTemplateForm.question.formLabels.question.questionType') }}
                  </label>
                  <Select v-model="question.questionType" :options="questionTypesForOptions" />
                </div>
                <div v-if="hasOption(question.questionType)" class="formParts">
                  <label class="c-formLabel">
                    {{ $t('components.CompaniesSurveyTemplateForm.question.formLabels.question.options') + '（' + $t('components.CompaniesSurveyTemplateForm.maxValidation', { value: 20 }) + '）' }}
                  </label>
                  <div v-for="(option, optionIndex) in question.options" :key="'question_' + index + '_option' + optionIndex" class="option">
                    <ValidationProvider
                      v-slot="{ errors }"
                      v-model="option.name"
                      :name="$t('components.CompaniesSurveyTemplateForm.question.formLabels.question.options')"
                      :rules="'required'"
                      :vid="'question_' + index + '_option' + optionIndex"
                      class="option__input"
                    >
                      <TextField v-model="option.name" class="c-textField" :placeholder="$t('messages.noSettings')" />
                      <ValidationError :error="errors[0]" />
                    </ValidationProvider>
                    <button v-show="canRemoveOption(question.options)" type="button" class="c-button__item" @click="removeOption(index, optionIndex)">
                      <Icon name="iconCross" class="c-button__icon option__removeBtn" />
                    </button>
                  </div>
                  <button v-show="canAddOption(question.options)" class="formParts" type="button" @click="addOption(index)">
                    <Icon name="iconPlusRed" class="c-button__icon" />
                    <span class="addLabel">{{ $t('labels.add', { value: $t('components.CompaniesSurveyTemplateForm.question.formLabels.question.options') }) }}</span>
                  </button>
                </div>
              </template>
            </KkBoxWithMenu>
          </div>
          <button type="button" class="addQuestionBox" @click="addQuestion">
            <Icon name="iconPlusRed" class="addQuestionBox__icon" />
            <p>{{ $t('components.CompaniesSurveyTemplateForm.question.addQuestion') }}</p>
          </button>
          <CompaniesSurveyTemplateFormBorderBox :title="$t('components.CompaniesSurveyTemplateForm.question.nps.title')" class="question">
            <Info>
              {{ $t('components.CompaniesSurveyTemplateForm.question.nps.info') }}
            </Info>
            <div class="formParts">
              <input
                id="checkbox__checkNps"
                :checked="showNps"
                class="c-checkbox__input"
                type="checkbox"
                @change="() => changeQuestionAttribute(!showNps, 'showNps')"
              >
              <label class="c-checkbox__label" for="checkbox__checkNps">
                {{ $t('components.CompaniesSurveyTemplateForm.question.nps.checkNps') }}
              </label>
            </div>
            <template v-if="showNps">
              <div class="formParts">
                <ValidationProvider v-slot="{ errors }"
                                    v-model="nps.name"
                                    :name="$t('components.CompaniesSurveyTemplateForm.question.formLabels.question.name')"
                                    :rules="'required|max:' + maxLength['name']"
                >
                  <label class="c-formLabel">
                    {{ labelTextFormat({ label: 'question.name', max: maxLength['name'], required: true, current: nps.name }) }}
                  </label>
                  <TextField
                    :value="nps.name"
                    class="c-textField"
                    :placeholder="$t('messages.noSettings')" @input="e => changeQuestionAttribute(e.target.value, 'nps.name')"
                  />
                  <ValidationError :error="errors[0]" />
                </ValidationProvider>
              </div>
              <div class="formParts">
                <ValidationProvider v-slot="{ errors }"
                                    v-model="nps.description"
                                    :name="$t('components.CompaniesSurveyTemplateForm.question.formLabels.question.description')"
                                    :rules="'max:' + maxLength['description']"
                >
                  <label class="c-formLabel">
                    {{ labelTextFormat({ label: 'question.description', max: maxLength['description'], required: false, current: nps.description }) }}
                  </label>
                  <TextField
                    :value="nps.description"
                    class="c-textField"
                    :placeholder="$t('messages.noSettings')"
                    @input="e => changeQuestionAttribute(e.target.value, 'nps.description')"
                  />
                  <ValidationError :error="errors[0]" />
                </ValidationProvider>
              </div>
              <div class="formParts">
                <input
                  id="checkbox__npsRequired"
                  :checked="nps.required"
                  class="c-checkbox__input"
                  type="checkbox"
                  @change="() => changeQuestionAttribute(!nps.required, 'nps.required')"
                >
                <label class="c-checkbox__label" for="checkbox__npsRequired">
                  {{ $t('components.CompaniesSurveyTemplateForm.required') }}
                </label>
              </div>
            </template>
          </CompaniesSurveyTemplateFormBorderBox>
        </template>
      </div>
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
        <PreviewContainer :title="$t('components.CompaniesSurveyTemplateForm.question.previewName')">
          <Loading v-if="companiesCustomSurveyLoading" />
          <SurveyInputFormPreview v-else :survey="companiesCustomSurvey" :no-header="true" />
        </PreviewContainer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import TextField from '../TextField'
import ValidationError from '../ValidationError'
import Select from '../Select'
import Icon from '../Icon'
import CompaniesSurveyTemplateFormBorderBox from './BorderBox'
import Info from '../Info'
import Loading from '../Loading'
import PreviewContainer from '../PreviewContainer'
import SurveyInputFormPreview from '../SurveyInputFormPreview'
import CompaniesSurveyTemplateFormDisabledQuestion from './DisabledQuestion'
import { KkBoxWithMenu } from '../base'

export default {
  name: 'CompaniesSurveyTemplateFormQuestion',
  components: {
    CompaniesSurveyTemplateFormDisabledQuestion,
    Info,
    Loading,
    PreviewContainer,
    SurveyInputFormPreview,
    CompaniesSurveyTemplateFormBorderBox,
    Select,
    KkBoxWithMenu,
    TextField,
    ValidationError,
    Icon
  },
  computed: {
    ...mapState({
      companiesCustomSurvey: state => state.companiesCustomSurvey.data,
      companiesCustomSurveyLoading: state => state.companiesCustomSurvey.loading,
      questions: state => state.companiesCustomSurvey.data.question.questions,
      nps: state => state.companiesCustomSurvey.data.question.nps,
      showNps: state => state.companiesCustomSurvey.data.question.showNps,
      meta: state => state.companiesCustomSurvey.meta
    }),
    menuList() {
      return [
        {
          type: 'moveTop',
          text: this.$t('components.CompaniesSurveyTemplateForm.question.item.editMenu.moveTop')
        },
        {
          type: 'moveOneUp',
          text: this.$t('components.CompaniesSurveyTemplateForm.question.item.editMenu.moveOneUp')
        },
        {
          type: 'moveOneDown',
          text: this.$t('components.CompaniesSurveyTemplateForm.question.item.editMenu.moveOneDown')
        },
        {
          type: 'moveBottom',
          text: this.$t('components.CompaniesSurveyTemplateForm.question.item.editMenu.moveBottom')
        },
        {
          type: 'delete',
          text: this.$t('components.CompaniesSurveyTemplateForm.question.item.editMenu.delete')
        },
      ]
    },
    questionTypesForOptions() {
      return {
        [this.$t('components.CompaniesSurveyTemplateForm.question.questionType.oneLineText')]: 'one_line_text',
        [this.$t('components.CompaniesSurveyTemplateForm.question.questionType.text')]: 'text',
        [this.$t('components.CompaniesSurveyTemplateForm.question.questionType.radio')]: 'radio',
        [this.$t('components.CompaniesSurveyTemplateForm.question.questionType.checkbox')]: 'checkbox',
        [this.$t('components.CompaniesSurveyTemplateForm.question.questionType.date')]: 'date',
      }
    },
    maxLength() {
      return {
        name: 140,
        description: 140,
      }
    },
  },
  methods: {
    ...mapActions({
      changeAttribute: 'companiesCustomSurvey/changeAttribute'
    }),
    labelTextFormat({ label, max = 0, current, required = true }) {
      let text = this.$t('components.CompaniesSurveyTemplateForm.question.formLabels.' + label)
      if (max !== 0) {
        text += this.$t('components.CompaniesSurveyTemplateForm.labelCount', { current: current.length , max: max })
      }
      text += required ? this.$t('labels.requiredLabel') : this.$t('labels.optionalLabel')
      return text
    },
    hasOption(type) {
      return type === 'radio' || type === 'checkbox'
    },
    canAddOption(options) {
      return options.length < 20
    },
    addOption(index) {
      this.questions[index].options.push({
        name: ''
      })
      this.changeQuestionAttribute(this.questions, 'questions')
    },
    canRemoveOption(options) {
      return options.length > 1
    },
    removeOption(questionIndex, optionIndex) {
      this.questions[questionIndex].options.splice(optionIndex, 1)
      this.changeQuestionAttribute(this.questions, 'questions')
    },
    addQuestion() {
      this.questions.push({
        name: '',
        description: '',
        required: false,
        questionType: 'one_line_text',
        options: [{ name: '' }]
      })
      this.changeQuestionAttribute(this.questions, 'questions')
    },
    handleClickMenu({ type, index }) {
      if (this.questions.length === 1) return
      if (type === 'moveOneUp' && index === 0) return
      if (type === 'moveBottom' && index === this.questions.length - 1) return

      const question = this.questions[index]
      this.questions.splice(index, 1)
      switch (type) {
      case 'moveTop':
        this.questions.unshift(question)
        break
      case 'moveOneUp':
        if (index === 0) return
        this.questions.splice(index - 1, 0, question)
        break
      case 'moveOneDown':
        this.questions.splice(index + 1, 0, question)
        break
      case 'moveBottom':
        this.questions.push(question)
        break
      case 'delete':
        break
      }
      this.changeQuestionAttribute(this.questions, 'questions')
    },
    changeQuestionAttribute(value, key) {
      if (key.includes('.')) {
        const [ firstKey, secondKey ] = key.split('.')
        this[firstKey][secondKey] = value
        this.changeAttribute({ tab: 'question', key: firstKey, value: this[firstKey] })
        return
      }
      this.changeAttribute({ tab: 'question', key: key, value: value })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.question {
  margin-top: 32px;
}
.formParts:not(:first-child) {
  margin-top: 16px;
}
.addLabel {
  color: $color-accent-primary;
}
.option {
  display: flex;
  &__input {
    width: 90%;
  }
  &__removeBtn {
    width: 16px;
  }
}
.addQuestionBox {
  margin-top: 32px;
  height: 160px;
  width: 100%;
  color: $color-accent-primary;
  background: $color-base-i;
  border-radius: 4px;
  text-align: center;
}
</style>
